import React, { useEffect, useRef, useState } from "react";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const normalizeComparableId = value => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

// Autocomplete de usuário (responsável) reutilizável — usado tanto no
// modal de criação quanto no filtro da listagem. Busca debounced em
// GET /users, protegida contra resposta fora de ordem (requestId) e
// contra atualização de estado após desmontagem (isMounted).
const TaskUserAutocomplete = ({
  value,
  onChange,
  label,
  required = false,
  disabled = false,
  error = false,
  helperText,
  excludeIds = []
}) => {
  const [options, setOptions] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [loading, setLoading] = useState(false);

  const isMounted = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);

    const delayDebounceFn = setTimeout(async () => {
      try {
        const { data } = await api.get("/users", { params: { searchParam } });

        if (!isMounted.current || currentRequestId !== requestIdRef.current) return;
        setOptions(data.users || []);
      } catch (err) {
        if (!isMounted.current || currentRequestId !== requestIdRef.current) return;
        toastError(err);
      } finally {
        if (isMounted.current && currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  const excludedIdSet = new Set(excludeIds.map(normalizeComparableId).filter(Boolean));
  const filteredOptions =
    excludedIdSet.size > 0
      ? options.filter(option => !excludedIdSet.has(normalizeComparableId(option.id)))
      : options;

  return (
    <Autocomplete
      options={filteredOptions}
      value={value || null}
      loading={loading}
      disabled={disabled}
      getOptionLabel={option => (option && option.name) || ""}
      getOptionSelected={(option, val) => option.id === val.id}
      noOptionsText={i18n.t("tasks.modal.noOptions")}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={(_, newInputValue) => setSearchParam(newInputValue)}
      fullWidth
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
};

export default TaskUserAutocomplete;
